/**
 * Lesezugriff auf das geöffnete Word-Dokument.
 *
 * Alle Kursprüfungen arbeiten auf einer "Momentaufnahme" des Dokuments statt
 * direkt auf der Office.js-API. Das hat zwei Vorteile: die einzelnen Lektionen
 * bleiben frei von Office.js-Details und pro Prüfung wird nur ein einziger
 * Datenabruf benötigt.
 */

export interface FontState {
  /** `null`, wenn der Bereich gemischt formatiert ist. */
  name: string | null;
  size: number | null;
  bold: boolean | null;
  italic: boolean | null;
  /** Word-Werte wie "None", "Single", "Double". */
  underline: string | null;
  color: string | null;
  highlightColor: string | null;
}

export interface TextRun extends FontState {
  text: string;
  /** Index des Absatzes, zu dem der Textabschnitt gehört. */
  paragraphIndex: number;
}

/** Absatzformatierung. Alle Längen in Punkt (pt). */
export interface ParagraphFormat {
  /** "Left", "Centered", "Right", "Justified" – oder "Mixed" bei Uneinheitlichkeit. */
  alignment: string | null;
  spaceBefore: number | null;
  spaceAfter: number | null;
  /**
   * Zeilenabstand in Punkt. Word rechnet den Wert für die Anzeige durch 12:
   * 12 entspricht "einfach", 18 entspricht "1,5 Zeilen", 24 "doppelt".
   */
  lineSpacing: number | null;
  leftIndent: number | null;
  rightIndent: number | null;
  firstLineIndent: number | null;
}

export interface ListInfo {
  /** Gliederungsebene der Liste, beginnend bei 0. */
  level: number;
  /** Das angezeigte Aufzählungszeichen bzw. die Nummer, z. B. "1.2." oder "•". */
  listString: string;
}

export interface ParagraphInfo {
  index: number;
  text: string;
  /** Formatierung des gesamten Absatzes (gemischte Werte sind `null`). */
  font: FontState;
  /** Sprachunabhängiger Name der Formatvorlage, z. B. "Normal" oder "Heading1". */
  styleBuiltIn: string;
  /** Angezeigter Name der Formatvorlage, in deutschem Word z. B. "Überschrift 1". */
  styleName: string;
  /** Absatzformatierung: Ausrichtung, Abstände, Einzüge. */
  format: ParagraphFormat;
  /** Gesetzt, wenn der Absatz Teil einer Aufzählung oder Nummerierung ist. */
  list: ListInfo | null;
  /** Wortweise Formatierung – nötig, sobald einzelne Wörter geprüft werden. */
  runs: TextRun[];
}

export interface DocumentSnapshot {
  /** Gesamter Dokumenttext. */
  text: string;
  /** Alle Absätze inklusive leerer Absätze. */
  paragraphs: ParagraphInfo[];
  /** Alle Absätze mit sichtbarem Inhalt. */
  filledParagraphs: ParagraphInfo[];
  /** Alle Wörter des Dokuments mit ihrer jeweiligen Formatierung. */
  runs: TextRun[];
}

const FONT_PROPERTIES = [
  "font/name",
  "font/size",
  "font/bold",
  "font/italic",
  "font/underline",
  "font/color",
  "font/highlightColor",
];

const FORMAT_PROPERTIES = [
  "alignment",
  "spaceBefore",
  "spaceAfter",
  "lineSpacing",
  "leftIndent",
  "rightIndent",
  "firstLineIndent",
  "isListItem",
];

const PARAGRAPH_PROPERTIES = [
  "text",
  "style",
  "styleBuiltIn",
  ...FORMAT_PROPERTIES,
  ...FONT_PROPERTIES,
]
  .map((property) => `items/${property}`)
  .join(",");

const RANGE_PROPERTIES = ["text", ...FONT_PROPERTIES]
  .map((property) => `items/${property}`)
  .join(",");

/** Fehler, der geworfen wird, wenn das Add-in nicht in Word läuft. */
export class WordUnavailableError extends Error {
  constructor() {
    super(
      "Es ist gerade kein Word-Dokument verbunden. Bitte öffne den Kurs über das " +
        "Add-in-Panel in Word.",
    );
    this.name = "WordUnavailableError";
  }
}

export function isWordAvailable(): boolean {
  return typeof Word !== "undefined" && typeof Word.run === "function";
}

function toFontState(font: Word.Font): FontState {
  return {
    name: normalizeString(font.name),
    size: typeof font.size === "number" ? font.size : null,
    bold: typeof font.bold === "boolean" ? font.bold : null,
    italic: typeof font.italic === "boolean" ? font.italic : null,
    underline: normalizeString(font.underline),
    color: normalizeString(font.color),
    highlightColor: normalizeString(font.highlightColor),
  };
}

function toNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function toParagraphFormat(paragraph: Word.Paragraph): ParagraphFormat {
  return {
    alignment: normalizeString(paragraph.alignment),
    spaceBefore: toNumber(paragraph.spaceBefore),
    spaceAfter: toNumber(paragraph.spaceAfter),
    lineSpacing: toNumber(paragraph.lineSpacing),
    leftIndent: toNumber(paragraph.leftIndent),
    rightIndent: toNumber(paragraph.rightIndent),
    firstLineIndent: toNumber(paragraph.firstLineIndent),
  };
}

function normalizeString(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/** Liest das gesamte Dokument aus und bereitet es für die Prüfungen auf. */
export async function readDocument(): Promise<DocumentSnapshot> {
  if (!isWordAvailable()) throw new WordUnavailableError();

  return Word.run(async (context) => {
    const paragraphCollection = context.document.body.paragraphs;
    paragraphCollection.load(PARAGRAPH_PROPERTIES);
    await context.sync();

    const items = paragraphCollection.items;

    // Wortweise Formatierung nur für Absätze mit Inhalt anfordern – für leere
    // Absätze liefert Word ohnehin keine Textbereiche.
    const rangeRequests = items.map((paragraph) => {
      if (!paragraph.text || paragraph.text.trim().length === 0) return null;
      const ranges = paragraph.getTextRanges([" "], false);
      ranges.load(RANGE_PROPERTIES);
      return ranges;
    });

    // Die Listenebene liegt an einem eigenen Objekt und lässt sich erst
    // anfordern, wenn feststeht, welche Absätze überhaupt Listenelemente sind.
    const listRequests = items.map((paragraph) => {
      if (paragraph.isListItem !== true) return null;
      const listItem = paragraph.listItemOrNullObject;
      listItem.load("level,listString,isNullObject");
      return listItem;
    });
    await context.sync();

    const paragraphs: ParagraphInfo[] = items.map((paragraph, index) => {
      const ranges = rangeRequests[index];
      const runs: TextRun[] = (ranges?.items ?? [])
        .map((range) => ({
          text: (range.text ?? "").trim(),
          paragraphIndex: index,
          ...toFontState(range.font),
        }))
        .filter((run) => run.text.length > 0);

      const listItem = listRequests[index];
      const list: ListInfo | null =
        listItem && listItem.isNullObject !== true && typeof listItem.level === "number"
          ? {
              level: listItem.level,
              listString: typeof listItem.listString === "string" ? listItem.listString.trim() : "",
            }
          : null;

      return {
        index,
        text: (paragraph.text ?? "").trim(),
        font: toFontState(paragraph.font),
        format: toParagraphFormat(paragraph),
        list,
        styleBuiltIn: normalizeString(paragraph.styleBuiltIn) ?? "Normal",
        styleName: normalizeString(paragraph.style) ?? "Standard",
        runs,
      };
    });

    const filledParagraphs = paragraphs.filter((paragraph) => paragraph.text.length > 0);

    return {
      text: paragraphs.map((paragraph) => paragraph.text).join("\n"),
      paragraphs,
      filledParagraphs,
      runs: paragraphs.flatMap((paragraph) => paragraph.runs),
    };
  });
}

/** Zählt die Wörter im Dokument. */
export function countWords(snapshot: DocumentSnapshot): number {
  return snapshot.runs.filter((run) => /\p{L}|\p{N}/u.test(run.text)).length;
}

/** Alle im Dokument verwendeten Schriftarten (ohne Mehrfachnennungen). */
export function usedFontNames(snapshot: DocumentSnapshot): string[] {
  const names = new Set<string>();
  for (const run of snapshot.runs) {
    if (run.name) names.add(run.name);
  }
  return [...names].sort((a, b) => a.localeCompare(b, "de"));
}

/** Alle im Dokument verwendeten Schriftgrößen. */
export function usedFontSizes(snapshot: DocumentSnapshot): number[] {
  const sizes = new Set<number>();
  for (const run of snapshot.runs) {
    if (typeof run.size === "number") sizes.add(run.size);
  }
  return [...sizes].sort((a, b) => a - b);
}

export function isUnderlined(run: FontState): boolean {
  return run.underline !== null && run.underline.toLowerCase() !== "none";
}

export function hasHighlight(run: FontState): boolean {
  const value = run.highlightColor?.toLowerCase();
  return Boolean(value && value !== "none" && value !== "#ffffff");
}

/** Vergleicht Schriftartnamen unabhängig von Groß-/Kleinschreibung und Leerzeichen. */
export function isFont(run: FontState, fontName: string): boolean {
  if (!run.name) return false;
  return run.name.replace(/\s+/g, "").toLowerCase() === fontName.replace(/\s+/g, "").toLowerCase();
}

/* ---------- Formatvorlagen ---------- */

/** Alle Absätze, denen eine bestimmte Formatvorlage zugewiesen ist. */
export function paragraphsWithStyle(
  snapshot: DocumentSnapshot,
  styleBuiltIn: string,
): ParagraphInfo[] {
  return snapshot.filledParagraphs.filter(
    (paragraph) => paragraph.styleBuiltIn.toLowerCase() === styleBuiltIn.toLowerCase(),
  );
}

/**
 * Ebene einer Überschrift, also 1 für "Heading1". Für alle anderen
 * Formatvorlagen `null`.
 */
export function headingLevel(paragraph: ParagraphInfo): number | null {
  const match = /^Heading([1-9])$/i.exec(paragraph.styleBuiltIn);
  return match ? Number(match[1]) : null;
}

export interface HeadingInfo {
  level: number;
  paragraph: ParagraphInfo;
}

/** Alle Überschriften des Dokuments in Dokumentreihenfolge. */
export function headings(snapshot: DocumentSnapshot): HeadingInfo[] {
  return snapshot.filledParagraphs
    .map((paragraph) => {
      const level = headingLevel(paragraph);
      return level === null ? null : { level, paragraph };
    })
    .filter((entry): entry is HeadingInfo => entry !== null);
}

/**
 * Prüft, ob die Überschriftenebenen lückenlos aufeinander aufbauen. Nach einer
 * Überschrift 1 darf also eine 2 folgen, aber keine 3.
 */
export function hasHeadingLevelGap(snapshot: DocumentSnapshot): boolean {
  const levels = headings(snapshot).map((entry) => entry.level);
  if (levels.length === 0) return false;
  if (levels[0] !== 1) return true;
  return levels.some((level, index) => index > 0 && level > levels[index - 1] + 1);
}

/** Enthält das Dokument ein eingefügtes Inhaltsverzeichnis? */
export function hasTableOfContents(snapshot: DocumentSnapshot): boolean {
  return snapshot.filledParagraphs.some((paragraph) => /^Toc[1-9]$/i.test(paragraph.styleBuiltIn));
}

/**
 * Sind alle übergebenen Absätze gleich formatiert? Das ist der sichtbare Beweis
 * dafür, dass eine Formatvorlage statt manueller Formatierung verwendet wurde.
 */
export function hasUniformFormatting(paragraphs: ParagraphInfo[]): boolean {
  const runs = paragraphs.flatMap((paragraph) => paragraph.runs);
  if (runs.length === 0) return false;
  const [first] = runs;
  return runs.every(
    (run) => run.name === first.name && run.size === first.size && run.bold === first.bold,
  );
}

/* ---------- Absatzformatierung und Listen ---------- */

/**
 * Längster Block aus aufeinanderfolgenden leeren Absätzen.
 *
 * Leerzeilen sind der häufigste Ersatz für richtige Absatzabstände und
 * Seitenumbrüche – dieser Wert macht sie sichtbar.
 */
export function longestEmptyParagraphRun(snapshot: DocumentSnapshot): number {
  let longest = 0;
  let current = 0;
  for (const paragraph of snapshot.paragraphs) {
    if (paragraph.text.length === 0) {
      current += 1;
      longest = Math.max(longest, current);
    } else {
      current = 0;
    }
  }
  return longest;
}

/** Alle Absätze, die Teil einer Aufzählung oder Nummerierung sind. */
export function listParagraphs(snapshot: DocumentSnapshot): ParagraphInfo[] {
  return snapshot.filledParagraphs.filter((paragraph) => paragraph.list !== null);
}

/** Alle im Dokument vorkommenden Listenebenen, aufsteigend sortiert. */
export function listLevels(snapshot: DocumentSnapshot): number[] {
  const levels = new Set<number>();
  for (const paragraph of listParagraphs(snapshot)) {
    if (paragraph.list) levels.add(paragraph.list.level);
  }
  return [...levels].sort((a, b) => a - b);
}

/**
 * Beginnt ein Absatz mit Zeichen, die eine Liste nur nachahmen? Typisch sind
 * von Hand getippte Striche oder Sternchen am Zeilenanfang.
 */
export function looksLikeManualList(paragraph: ParagraphInfo): boolean {
  return paragraph.list === null && /^\s*([-*•‣]|\d+[.)])\s+/.test(paragraph.text);
}

/** Enthält der Absatz mehrere Leerzeichen oder Tabulatoren hintereinander? */
export function hasManualSpacing(paragraph: ParagraphInfo): boolean {
  return /(\t| {3,})/.test(paragraph.text);
}

/** Ist der Absatz Teil einer nummerierten Liste (statt einer Aufzählung)? */
export function isNumberedListItem(paragraph: ParagraphInfo): boolean {
  return paragraph.list !== null && /\d/.test(paragraph.list.listString);
}

/** Überschriften, die über eine Listenvorlage nummeriert sind. */
export function numberedHeadings(snapshot: DocumentSnapshot): ParagraphInfo[] {
  return headings(snapshot)
    .map((eintrag) => eintrag.paragraph)
    .filter(isNumberedListItem);
}
